import { ApolloError, gql, useQuery } from "@apollo/client";
import { ShipmentStopType } from "@api/graphql/generated/generated-types";
import { getUserFacingErrorMessage } from "@src/common/lib/NetworkErrorHandling";

const REVERT_STOP_COMPLETION_MODAL_SHIPMENT_QUERY = gql`
  query RevertStopCompletionModalShipment($courierId: ID!, $shipmentId: ID!) {
    shipmentById(courierId: $courierId, shipmentId: $shipmentId) {
      id
      stops {
        id
        type
        completedAt
        dispatchZone
        streetAddress
        city
        companyName
        tasks {
          sequenceNumber
        }
      }
    }
  }
`;

export interface RevertStopCompletionModalStop {
  id: string;
  type: ShipmentStopType;
  completedAt?: number | null;
  dispatchZone?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  companyName?: string | null;
  tasks?: Array<{
    sequenceNumber: number;
  }> | null;
}

export interface RevertStopCompletionModalShipment {
  id: string;
  stops: RevertStopCompletionModalStop[];
}

interface RevertStopCompletionModalShipmentQueryData {
  shipmentById: RevertStopCompletionModalShipment | null;
}

interface RevertStopCompletionModalShipmentQueryVariables {
  courierId: string;
  shipmentId: string;
}

interface UseRevertStopCompletionModalShipmentOptions {
  courierId: string | null | undefined;
  shipmentId: string;
  skip?: boolean;
}

interface UseRevertStopCompletionModalShipmentResult {
  shipment: RevertStopCompletionModalShipment | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  errorMessage?: string;
}

export function useRevertStopCompletionModalShipment({
  courierId,
  shipmentId,
  skip = false,
}: UseRevertStopCompletionModalShipmentOptions): UseRevertStopCompletionModalShipmentResult {
  const { data, loading, error } = useQuery<
    RevertStopCompletionModalShipmentQueryData,
    RevertStopCompletionModalShipmentQueryVariables
  >(REVERT_STOP_COMPLETION_MODAL_SHIPMENT_QUERY, {
    variables: { courierId: courierId ?? "", shipmentId },
    skip: skip || !courierId || !shipmentId,
    fetchPolicy: "network-only",
  });

  return {
    shipment: data?.shipmentById ?? undefined,
    loading,
    error,
    errorMessage: getUserFacingErrorMessage(error),
  };
}
